import React from 'react';
import { Calendar, CheckSquare, Users, Target, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import InfoIcon from './InfoIcon';
import ScopeDot from './ScopeDot';
import {
  buildCommittedProposal, getItemRoute, isItemDueToday,
  ES_DAY_LABEL, ITEM_TYPE_ICON, PRIORITY_COLOR,
} from './dashboardHelpers';

const WeeklyAgendaMinus = ({
  planDistribution,
  operationalAgenda,
  workdays,
  todayStr,
  navigate,
  weekOffset = 0,
  onWeekOffsetChange,
  nonWorkingDays = [],
  onToggleNonWorking,
  loading = false,
  weekStart,
}) => {
  const isNextWeek = weekOffset === 1;
  const rangeLabel = weekStart ? (() => {
    try {
      const start = new Date(`${weekStart}T00:00:00`);
      const end = new Date(start); end.setDate(start.getDate() + 4);
      const fmt = (d) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
      return `${fmt(start)} - ${fmt(end)}`;
    } catch { return ''; }
  })() : '';
  const nonWorkingSet = new Set(nonWorkingDays || []);
  return (
    <Card data-testid="weekly-agenda-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              {isNextWeek ? 'Agenda semana siguiente (Lun-Vie)' : 'Agenda semanal (Lun-Vie)'}
              {isNextWeek && rangeLabel && <span className="ml-1 text-muted-foreground font-normal">· {rangeLabel}</span>}
            </span>
            <InfoIcon text="Cada día muestra primero lo que ya tienes comprometido (reuniones, llamadas, tareas y seguimientos con fecha) y después una propuesta automática de hasta 3 contactos a cultivar. La propuesta no es editable: si un día es festivo, márcalo y sus acciones se contabilizan como no realizables." testid="info-weekly-agenda" ariaLabel="Información sobre Agenda semanal" />
            {isNextWeek && <span className="text-[10px] text-muted-foreground italic ml-1 hidden md:inline">Propuesta preliminar</span>}
          </span>
          <div className="flex rounded-md border p-0.5 bg-muted/30 flex-shrink-0" data-testid="agenda-week-toggle">
            <button
              type="button"
              onClick={() => onWeekOffsetChange && onWeekOffsetChange(0)}
              className={`h-6 px-2 text-[11px] rounded ${weekOffset === 0 ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="agenda-week-current"
            >
              Esta semana
            </button>
            <button
              type="button"
              onClick={() => onWeekOffsetChange && onWeekOffsetChange(1)}
              className={`h-6 px-2 text-[11px] rounded ${weekOffset === 1 ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="agenda-week-next"
            >
              Próxima
            </button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-5 gap-2">
          {workdays.map((day, idx) => {
            const items = planDistribution?.[day] || [];
            const { committed, proposal, totalCount } = buildCommittedProposal(day, operationalAgenda, items);
            const isToday = day === todayStr && weekOffset === 0;
            const isNonWorking = nonWorkingSet.has(day);
            const dayNumber = day.slice(8, 10);
            const committedVisible = committed.slice(0, 3);
            return (
              <div
                key={day}
                className={`relative group p-2 rounded-md border min-h-[95px] ${isToday ? 'bg-primary/10 border-primary' : 'bg-muted/30'} ${isNonWorking ? 'opacity-60' : ''} ${loading ? 'animate-pulse' : ''}`}
                data-testid={`agenda-day-${day}`}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <span className={`text-[10px] font-medium ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                    {ES_DAY_LABEL[idx]}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs ${isToday ? 'font-bold text-primary' : 'text-muted-foreground'}`}>
                      {dayNumber}
                    </span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-foreground p-0.5 rounded transition-opacity"
                          data-testid={`agenda-day-menu-${day}`}
                          aria-label={`Menú del día ${day}`}
                        >
                          <MoreHorizontal className="w-3 h-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-1" align="end">
                        <button
                          type="button"
                          onClick={() => onToggleNonWorking && onToggleNonWorking(day, !isNonWorking)}
                          className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-muted"
                          data-testid={`agenda-day-toggle-nw-${day}`}
                        >
                          {isNonWorking ? 'Quitar festivo' : 'Marcar como festivo / no laborable'}
                        </button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                {isNonWorking && (
                  <p className="text-[9px] text-muted-foreground italic mb-1" data-testid={`agenda-day-nw-label-${day}`}>
                    Festivo - objetivo no alcanzable
                  </p>
                )}
                {totalCount === 0 ? (
                  <p className="text-[10px] text-muted-foreground text-center mt-4">-</p>
                ) : (
                  <div className="space-y-1">
                    {/* BLOQUE 1: COMPROMETIDO */}
                    {committed.length > 0 && (
                      <div className="space-y-0.5" data-testid={`day-committed-block-${day}`}>
                        {committedVisible.map((it, i) => {
                          const Icon = it.__kind === 'activity' ? Users : it.__kind === 'deadline' || it.__kind === 'urgent_opp' ? Target : CheckSquare;
                          const strike = isNonWorking;
                          const overdue = it.is_overdue;
                          return (
                            <button
                              key={`cmt-${day}-${it.item_type}-${it.item_id}-${i}`}
                              type="button"
                              onClick={() => navigate(getItemRoute(it))}
                              className={`w-full flex items-center gap-1 text-left text-[11px] truncate hover:underline ${strike ? 'line-through opacity-50' : ''} ${overdue ? 'text-red-600' : ''}`}
                            >
                              <Icon className="w-3 h-3 flex-shrink-0" />
                              <ScopeDot scope={it.scope} className="mr-1" />
                              <span className="truncate">{it.title}</span>
                              {overdue && <span className="text-[9px] whitespace-nowrap">·{it.days_overdue}d</span>}
                            </button>
                          );
                        })}
                        {committed.length > 3 && (
                          <span className="text-[9px] text-muted-foreground">+{committed.length - 3} comprometido</span>
                        )}
                      </div>
                    )}
                    {/* BLOQUE 2: PROPUESTA */}
                    {proposal.length > 0 && (
                      <div className="mt-1.5 pt-1.5 border-t border-dashed border-muted-foreground/20 space-y-0.5" data-testid={`day-proposal-block-${day}`}>
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Propuesta</p>
                        {proposal.map((it) => {
                          const Icon = ITEM_TYPE_ICON[it.item_type] || CheckSquare;
                          const dueToday = isItemDueToday(it, todayStr);
                          const colorCls = dueToday ? 'text-orange-600' : PRIORITY_COLOR[it.priority] || 'text-blue-500';
                          const done = it.done || it.completed_in_plan;
                          const strike = isNonWorking || done;
                          return (
                            <button
                              key={`${day}-${it.item_type}-${it.item_id}`}
                              type="button"
                              onClick={() => navigate(getItemRoute(it))}
                              className={`w-full flex items-center gap-1 text-left text-[11px] truncate hover:underline ${strike ? 'line-through opacity-50' : ''}`}
                            >
                              <Icon className={`w-3 h-3 flex-shrink-0 ${colorCls}`} />
                              <ScopeDot scope={it.scope} className="mr-1" />
                              <span className="truncate">{it.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default WeeklyAgendaMinus;
